/**
 * CodeBlockDecoratorNode - WYSIWYG code block for Lexical
 *
 * Architecture (Obsidian Live Preview pattern):
 * - View mode  : SyntaxHighlighter + header (language, copy, collapse)
 * - Edit mode  : <textarea> — triggered by a click on the rendered block
 * - On blur    : saves code to node, switches back to view mode
 *
 * The node stores __code + __language as properties (not Lexical text content).
 * Markdown export  : ```lang\ncode\n```
 * Markdown import  : post-processed from CodeNode in WorkingDocEditor
 */

import { cn } from '@/lib/utils/cn';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { LexicalEditor, NodeKey, SerializedLexicalNode, Spread } from 'lexical';
import { $getNodeByKey, DecoratorNode } from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import {
  oneDark,
  dracula,
  nord,
  ghcolors,
  vscDarkPlus,
  solarizedlight,
  solarizedDarkAtom,
  materialDark,
  nightOwl,
  okaidia,
} from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Copy, Check, ChevronDown, AlignLeft, X } from 'lucide-react';
import type { ElementTransformer } from '@lexical/markdown';
import { useUIPrefsStore } from '@/app/stores/use-ui-prefs-store';
import { TextMark } from '@/shared/components';
import type { JSX } from 'react';
import { Icon } from '@/shared/ui';

// ─── Prism theme map ──────────────────────────────────────────────────────────

type PrismStyle = Record<string, React.CSSProperties>;

const PRISM_THEME_MAP: Record<string, PrismStyle> = {
  monokai: okaidia,
  dracula: dracula,
  'one-dark': oneDark,
  'github-light': ghcolors,
  'github-dark': vscDarkPlus,
  nord: nord,
  'solarized-light': solarizedlight,
  'solarized-dark': solarizedDarkAtom,
  material: materialDark,
  'tokyo-night': nightOwl,
};

// ─── Serialized type ──────────────────────────────────────────────────────────

/* Bouton d'en-tête : repli, copie, suppression, édition. La feuille les
   déclarait en deux classes jumelles (`.collapseBtn` et `.actionBtn`) au
   contenu identique. */
const ICON_BTN =
  'text-fg-muted hover:bg-bg-hover inline-flex size-[22px] shrink-0 cursor-pointer items-center justify-center rounded-sm border-0 bg-transparent p-0 opacity-60 transition-[opacity,background-color] hover:opacity-100';

/* Repli sans hauteur calculée : la grille passe de 1fr à 0fr. */
const BODY = 'grid grid-rows-[1fr] font-mono leading-relaxed transition-[grid-template-rows]';

export type SerializedCodeBlockDecoratorNode = Spread<
  { code: string; language: string; type: 'code-block-decorator'; version: 1 },
  SerializedLexicalNode
>;

// ─── React component ──────────────────────────────────────────────────────────

interface CodeBlockDecoratorComponentProps {
  nodeKey: NodeKey;
  code: string;
  language: string;
  autoFocus: boolean;
}

const CodeBlockDecoratorComponent = ({
  nodeKey,
  code,
  language,
  autoFocus,
}: CodeBlockDecoratorComponentProps) => {
  const [editor] = useLexicalComposerContext();

  // Local editable state — synced with node props on external changes (undo/redo)
  const [localCode, setLocalCode] = useState(code);
  const [localLang, setLocalLang] = useState(language || 'text');
  const [isEditing, setIsEditing] = useState(autoFocus);
  const [copied, setCopied] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  // Timer ref for debounced blur — prevents race-condition exit when Lexical
  // briefly steals focus back to the ContentEditable after editor.update().
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Prism theme ────────────────────────────────────────────────────────────
  const codeThemeId = useUIPrefsStore((s) => s.codeTheme);
  const codeBlocksCollapsedByDefault = useUIPrefsStore((s) => s.codeBlocksCollapsedByDefault);
  const codeBlocksShowLineNumbers = useUIPrefsStore((s) => s.codeBlocksShowLineNumbers);
  // Newly created blocks (autoFocus=true) always start expanded so the textarea can render.
  const [collapsed, setCollapsed] = useState(autoFocus ? false : codeBlocksCollapsedByDefault);
  // collapseAll / expandAll signals : disabled in v2 (no global command yet — V6+).
  const collapseAllSignal = 0;
  const expandAllSignal = 0;

  useEffect(() => {
    if (collapseAllSignal > 0) setCollapsed(true);
  }, [collapseAllSignal]);
  useEffect(() => {
    if (expandAllSignal > 0) setCollapsed(false);
  }, [expandAllSignal]);

  const prismStyle = useMemo(() => {
    const raw = PRISM_THEME_MAP[codeThemeId] ?? oneDark;
    const filtered: PrismStyle = {};
    for (const [sel, val] of Object.entries(raw)) {
      const isRoot = sel === 'pre[class*="language-"]' || sel === 'code[class*="language-"]';
      if (isRoot) {
        filtered[sel] = val;
      } else {
        const {
          background: _b,
          backgroundColor: _bc,
          ...rest
        } = val as React.CSSProperties & {
          background?: unknown;
          backgroundColor?: unknown;
        };
        filtered[sel] = rest;
      }
    }
    return filtered;
  }, [codeThemeId]);

  const rootStyle = prismStyle['pre[class*="language-"]'];
  const bgColor = (rootStyle?.background as string | undefined) ?? '#1e1e1e';
  // Text color from the Prism theme — ensures visibility regardless of app theme
  const textColor = (rootStyle?.color as string | undefined) ?? '#abb2bf';

  // ── Sync with node on undo/redo (only when not editing) ───────────────────
  useEffect(() => {
    if (!isEditing) setLocalCode(code);
  }, [code, isEditing]);

  useEffect(() => {
    if (!isEditing) setLocalLang(language || 'text');
  }, [language, isEditing]);

  // ── Cleanup blur timer on unmount ─────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    };
  }, []);

  // ── Persist code+lang to Lexical node ────────────────────────────────────
  const saveToNode = useCallback(
    (newCode: string, newLang: string) => {
      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        if ($isCodeBlockDecoratorNode(node)) {
          const w = node.getWritable();
          w.__code = newCode;
          w.__language = newLang;
          w.__autoFocus = false;
        }
      });
    },
    [editor, nodeKey],
  );

  // ── Edit mode ─────────────────────────────────────────────────────────────
  const enterEdit = useCallback(() => {
    setIsEditing(true);
    requestAnimationFrame(() => textareaRef.current?.focus());
  }, []);

  const exitEdit = useCallback(() => {
    setIsEditing(false);
    saveToNode(localCode, localLang);
    requestAnimationFrame(() => {
      editor.focus();
    });
  }, [localCode, localLang, saveToNode, editor]);

  // ── Blur handler ──────────────────────────────────────────────────────────
  // Two cases where we must NOT exit edit mode:
  // 1. Focus moved to another element inside the wrapper (e.g. lang input, buttons)
  // 2. Lexical briefly steals focus to ContentEditable after editor.update() —
  //    handled by the 50ms grace period + handleTextareaFocus cancellation.
  const handleTextareaBlur = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      const relatedTarget = e.relatedTarget as Node | null;
      if (wrapperRef.current && relatedTarget && wrapperRef.current.contains(relatedTarget)) {
        // Focus stayed inside the code block component — do nothing.
        return;
      }
      blurTimerRef.current = setTimeout(exitEdit, 50);
    },
    [exitEdit],
  );

  const handleTextareaFocus = useCallback(() => {
    if (blurTimerRef.current) {
      clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }
  }, []);

  // ── Copy ──────────────────────────────────────────────────────────────────
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(localCode);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch {
      /* ignore */
    }
  };

  // ── Delete the code block node ───────────────────────────────────────────
  const deleteNode = useCallback(() => {
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      if (!$isCodeBlockDecoratorNode(node)) return;
      const prevSibling = node.getPreviousSibling();
      const nextSibling = node.getNextSibling();
      node.remove();
      if (prevSibling) {
        prevSibling.selectEnd();
      } else if (nextSibling) {
        nextSibling.selectStart();
      }
    });
  }, [editor, nodeKey]);

  // ── Textarea key handling ─────────────────────────────────────────────────
  const handleTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const ta = textareaRef.current!;
    if (e.key === 'Tab') {
      e.preventDefault();
      const s = ta.selectionStart;
      const end = ta.selectionEnd;
      const newCode = localCode.substring(0, s) + '  ' + localCode.substring(end);
      setLocalCode(newCode);
      setTimeout(() => {
        ta.selectionStart = ta.selectionEnd = s + 2;
      }, 0);
    }
    if (e.key === 'Escape') {
      exitEdit();
    }
    // Backspace at position 0 → delete the code block (same as VSCode behavior)
    if (e.key === 'Backspace' && ta.selectionStart === 0 && ta.selectionEnd === 0) {
      e.preventDefault();
      deleteNode();
    }
  };

  const lineCount = localCode ? localCode.split('\n').length : 0;

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div
      ref={wrapperRef}
      className="relative my-2 overflow-hidden rounded-md text-sm select-none"
      style={{ '--code-bg': bgColor } as React.CSSProperties}
      contentEditable={false}
    >
      {/* Header */}
      <div className="flex items-center gap-1 bg-transparent py-[3px] pr-2 pl-1">
        <button
          type="button"
          className={cn(
            ICON_BTN,
            '[&_svg]:transition-transform',
            collapsed && '[&_svg]:-rotate-90',
          )}
          onClick={() => {
            setCollapsed((v) => !v);
          }}
          title={collapsed ? 'Développer' : 'Réduire'}
        >
          <Icon icon={ChevronDown} size="md" strokeWidth={2} />
        </button>

        {isEditing ? (
          <input
            value={localLang}
            onChange={(e) => {
              setLocalLang(e.target.value);
            }}
            className="text-primary min-w-0 flex-1 border-0 bg-transparent p-0 font-mono text-xs outline-none"
            placeholder="language"
            spellCheck={false}
          />
        ) : (
          <span className="text-fg-muted flex-1 truncate font-[inherit] text-xs">
            {collapsed ? <TextMark variant="highlight">{localLang}</TextMark> : localLang}
          </span>
        )}

        {!isEditing && (
          <span
            className="text-fg-muted inline-flex shrink-0 items-center gap-[3px] font-[inherit] text-xs opacity-55 select-none"
            title={`${lineCount} ligne${lineCount > 1 ? 's' : ''}`}
          >
            {lineCount}
            <Icon icon={AlignLeft} size="sm" strokeWidth={1.8} />
          </span>
        )}

        {isEditing ? (
          <>
            <button
              type="button"
              className={`${ICON_BTN} text-primary! opacity-100!`}
              onClick={exitEdit}
              title="Valider (Échap)"
            >
              <Icon icon={Check} size="md" strokeWidth={2.5} />
            </button>
            <button
              type="button"
              className={`${ICON_BTN} hover:text-error!`}
              onMouseDown={(e) => {
                e.preventDefault();
                deleteNode();
              }}
              title="Supprimer le bloc (⌫ en début)"
            >
              <Icon icon={X} size="md" strokeWidth={2} />
            </button>
          </>
        ) : (
          <button
            type="button"
            className={cn(ICON_BTN, copied && 'text-success! opacity-100!')}
            onClick={handleCopy}
            title={copied ? 'Copié !' : 'Copier'}
          >
            {copied ? (
              <Icon icon={Check} size="md" strokeWidth={2.5} />
            ) : (
              <Icon icon={Copy} size="md" strokeWidth={1.8} />
            )}
          </button>
        )}
      </div>

      {/* Body — always rendered, CSS grid controls collapse animation */}
      <div className={cn(BODY, collapsed && 'grid-rows-[0fr]')}>
        <div className="min-h-0 overflow-hidden bg-(--code-bg,var(--color-bg-subtle))">
          {isEditing ? (
            <textarea
              ref={textareaRef}
              value={localCode}
              onChange={(e) => {
                setLocalCode(e.target.value);
              }}
              onBlur={handleTextareaBlur}
              onFocus={handleTextareaFocus}
              onKeyDown={handleTextareaKeyDown}
              className="caret-primary tab-2 block min-h-20 w-full resize-none overflow-x-auto border-0 bg-transparent px-4 py-2 font-mono text-sm leading-relaxed whitespace-pre outline-none select-text"
              style={{ color: textColor }}
              // `autoFocus` ne vaut vrai que pour le bloc que l'utilisateur vient de
              // créer en éditant le document — pas au chargement de la page
              // (SAAS-915).
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus={autoFocus}
              spellCheck={false}
              autoComplete="off"
              rows={Math.max(lineCount, 3)}
            />
          ) : (
            <div
              className="cursor-text [&>div]:px-4! [&>div]:py-2!"
              onClick={enterEdit}
              title="Cliquer pour éditer"
            >
              <SyntaxHighlighter
                language={localLang || 'text'}
                style={prismStyle}
                PreTag="div"
                showLineNumbers={codeBlocksShowLineNumbers}
                customStyle={{
                  margin: 0,
                  borderRadius: 0,
                  fontSize: 'inherit',
                  lineHeight: 'inherit',
                  fontFamily: 'inherit',
                }}
                codeTagProps={{
                  style: {
                    fontFamily: 'inherit',
                    fontSize: 'inherit',
                    lineHeight: 'inherit',
                    background: 'transparent',
                  },
                }}
              >
                {localCode || ' '}
              </SyntaxHighlighter>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── DecoratorNode class ──────────────────────────────────────────────────────

export class CodeBlockDecoratorNode extends DecoratorNode<JSX.Element> {
  __code: string;
  __language: string;
  __autoFocus: boolean;

  static override getType(): string {
    return 'code-block-decorator';
  }

  static override clone(node: CodeBlockDecoratorNode): CodeBlockDecoratorNode {
    return new CodeBlockDecoratorNode(node.__code, node.__language, node.__autoFocus, node.__key);
  }

  constructor(code = '', language = 'text', autoFocus = false, key?: NodeKey) {
    super(key);
    this.__code = code;
    this.__language = language;
    this.__autoFocus = autoFocus;
  }

  static override importJSON(json: SerializedCodeBlockDecoratorNode): CodeBlockDecoratorNode {
    return new CodeBlockDecoratorNode(json.code, json.language, false);
  }

  override exportJSON(): SerializedCodeBlockDecoratorNode {
    return {
      type: 'code-block-decorator',
      version: 1,
      code: this.__code,
      language: this.__language,
    };
  }

  override createDOM(): HTMLElement {
    const div = document.createElement('div');
    return div;
  }

  override updateDOM(): boolean {
    return false;
  }

  override isInline(): boolean {
    return false;
  }

  override decorate(_editor: LexicalEditor): JSX.Element {
    return (
      <CodeBlockDecoratorComponent
        nodeKey={this.__key}
        code={this.__code}
        language={this.__language}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={this.__autoFocus}
      />
    );
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function $isCodeBlockDecoratorNode(node: unknown): node is CodeBlockDecoratorNode {
  return node instanceof CodeBlockDecoratorNode;
}

export function $createCodeBlockDecoratorNode(
  language = 'text',
  autoFocus = false,
): CodeBlockDecoratorNode {
  return new CodeBlockDecoratorNode('', language, autoFocus);
}

// ─── Markdown export transformer ─────────────────────────────────────────────
// NOTE: import is handled via CodeNode post-processing in WorkingDocEditor

export const CODE_BLOCK_DECORATOR_TRANSFORMER: ElementTransformer = {
  dependencies: [CodeBlockDecoratorNode],
  export: (node) => {
    if (!$isCodeBlockDecoratorNode(node)) return null;
    const lang = node.__language || '';
    return '```' + lang + '\n' + (node.__code || '') + '\n' + '```';
  },
  regExp: /^```(\w+)?/,
  replace: (_parentNode, _children, _match) => {
    // Import is done via CodeNode post-processing — this is export-only
  },
  type: 'element',
};
